/**
 * Options step 2 — overrides. Slots: a view over the slot rows whose amount
 * deviates from the default (edited on the Drop slots step). Lists (Phase
 * 18b): a list's own amount, gateway and send options, edited here.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { Badge } from '@/components/ui';

import { minutesLabel } from '../../../utils/format';
import { type SlotTime, slotKey } from '../../../utils/slot-times';
import type { ContainerEditor } from '../state/useContainerEditor';
import { ListOverridesSection } from './lists/ListOverridesSection';

interface Props {
  editor: ContainerEditor;
  /** Planned time per slot key (slotKey) — lane-aware since Phase 15. */
  timesByKey: Map<string, SlotTime>;
  countryKey: string;
}

const SUB = 'text-[10px] font-semibold uppercase tracking-wider text-muted-foreground';

export const OverridesStep: FC<Props> = ({ editor, timesByKey, countryKey }) => (
  <div className="flex flex-col gap-5">
    <section className="flex flex-col gap-2">
      <span className={SUB}>Slots</span>
      <SlotOverrides editor={editor} timesByKey={timesByKey} />
    </section>
    <section className="flex flex-col gap-2">
      <span className={SUB}>Lists</span>
      <ListOverridesSection editor={editor} countryKey={countryKey} />
    </section>
  </div>
);

const SlotOverrides: FC<Omit<Props, 'countryKey'>> = ({ editor, timesByKey }) => {
  const { draft } = editor;
  const overridden = draft.slots
    .map((slot, index) => ({ slot, index }))
    .filter(({ slot }) => slot.amount !== null);

  const timeOf = (index: number): string => {
    const time = timesByKey.get(slotKey(draft.slots[index], index));
    // The Options tab times the draft without a country day: these are Danish clock times (15/10/2026).
    return time && draft.slots[index].enabled ? `${minutesLabel(time.minute)} 🇩🇰` : '—';
  };

  if (overridden.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No slot overrides — every slot takes its list&apos;s or the container&apos;s
        amount. Type an amount on a slot row (Drop slots step) to override it.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="grid grid-cols-[90px_1fr_120px] gap-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        <span>Scope</span>
        <span>Target</span>
        <span>Amount</span>
      </div>
      {overridden.map(({ slot, index }) => (
        <div
          key={slot.id ?? index}
          className="grid grid-cols-[90px_1fr_120px] items-center gap-3 border-b border-border/30 py-1.5 text-sm last:border-b-0"
        >
          <Badge tone="info">slot</Badge>
          <span className="font-mono text-xs tabular-nums">
            Slot {index + 1} · {timeOf(index)}
          </span>
          <span className="font-mono text-xs tabular-nums">
            {slot.amount?.toLocaleString('en-US')}
          </span>
        </div>
      ))}
    </div>
  );
};
